import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaArrowLeft, FaQrcode, FaDownload, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa'; // Corrected to FaQrcode
import { QRCodeCanvas } from 'qrcode.react'; 
import { format, parseISO } from 'date-fns';

import PageLoader from '../helpers/PageLoader';
import NoDataAvailable from '../helpers/NoDataAvailable';
import CustomDatePicker from '../customStyles/CustomDatePicker';
import api from '../common/apiClient';
import SummaryApi from '../common/SummaryApi';

// Reusable Close icon
const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ExpoManagement = () => {
  const navigate = useNavigate();
  
  const [expos, setExpos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedExpo, setSelectedExpo] = useState(null); // Selected expo to view QR

  const [formData, setFormData] = useState({
    expoName: '',
    location: '',
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 3)) // default +3 days
  });

  const fetchExpos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllExpos.url);
      if (response.data.success) {
        setExpos(response.data.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Expo configurations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpos();
  }, [fetchExpos]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateExpo = async (e) => {
    e.preventDefault();
    if (!formData.expoName.trim()) {
      toast.error("Expo Name is required");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(SummaryApi.createExpo.url, formData);
      if (response.data.success) {
        toast.success("Expo registered and QR link created!");
        setShowCreateModal(false);
        setFormData({
          expoName: '',
          location: '',
          startDate: new Date(),
          endDate: new Date(new Date().setDate(new Date().getDate() + 3))
        });
        fetchExpos();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create Expo event");
    } finally {
      setSubmitting(false);
    }
  };

  // Pure Offline Client-side PNG Extraction from native HTML5 Canvas
  const downloadQrCode = (expo) => {
    const canvas = document.getElementById(`qr-canvas-${expo._id}`);
    if (!canvas) {
      toast.error("QR Code canvas could not be found locally.");
      return;
    }
    
    try {
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = url;
      link.download = `${expo.expoName.replace(/\s+/g, '_')}_QR_Code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download processing failed:", err);
      toast.error("Error occurred while generating PNG file.");
    }
  };

  return (
    <div className="w-full p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button onClick={() => navigate(-1)} className="mr-2 hover:bg-gray-200 rounded-full p-1 text-gray-700">
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">Expo QR Setup</h1>
            <p className="text-xs text-gray-500">Configure offline lead capture portals for upcoming exhibitions</p>
          </div>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm font-semibold shadow-sm"
        >
          <FaPlus /> Configure New Expo
        </button>
      </div>

      {loading ? (
        <PageLoader />
      ) : expos.length === 0 ? (
        <NoDataAvailable message="No Expo setups found" actionLabel="Create First Setup" onAction={() => setShowCreateModal(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {expos.map((expo) => (
            <div key={expo._id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-gray-800 line-clamp-1">{expo.expoName}</h3>
                  <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${expo.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                    {expo.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="text-red-500" />
                    <span>{expo.location || 'Stall Location Not Added'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="text-red-500" />
                    <span>
                      {format(parseISO(expo.startDate), "dd MMM")} - {format(parseISO(expo.endDate), "dd MMM yyyy")}
                    </span>
                  </div>
                </div>

                {/* 
                  Hidden Canvas for background PNG generation.
                  Allows instant offline high-res download without opening view modal first.
                */}
                <div className="hidden">
                  <QRCodeCanvas
                    id={`qr-canvas-${expo._id}`}
                    value={expo.qrCodeUrl || ''}
                    size={512} // High quality dimensions for printing
                    level={"H"} // Maximum error correction level
                    includeMargin={true}
                  />
                </div>
              </div>

              {/* Action Panels */}
              <div className="border-t border-gray-100 pt-4 flex gap-2">
                <button
                  onClick={() => setSelectedExpo(expo)}
                  className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
                >
                  <FaQrcode className="text-red-600" /> View QR Code
                </button>
                <button
                  onClick={() => downloadQrCode(expo)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
                >
                  <FaDownload /> Download PNG
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ==========================================
          MODAL 1: CONFIGURE NEW EXPO
         ========================================== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative">
            <button onClick={() => setShowCreateModal(false)} className="absolute right-4 top-4 hover:bg-gray-100 rounded-full p-1 text-gray-500">
              <CloseIcon />
            </button>
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaQrcode className="text-red-600" /> Setup Expo Capture Portal
            </h2>

            <form onSubmit={handleCreateExpo} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Expo / Event Name *</label>
                <input
                  type="text"
                  name="expoName"
                  required
                  value={formData.expoName}
                  onChange={handleInputChange}
                  placeholder="e.g. Education Expo 2026"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-red-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Stall Location / Details</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g. Hall 3, Stall A-22"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-red-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                  <CustomDatePicker value={formData.startDate} onChange={(date) => setFormData(prev => ({ ...prev, startDate: date }))} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                  <CustomDatePicker value={formData.endDate} onChange={(date) => setFormData(prev => ({ ...prev, endDate: date }))} />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold disabled:bg-gray-300"
                >
                  {submitting ? 'Setting up...' : 'Create QR Code'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL 2: VIEW OFFLINE QR CODE
         ========================================== */}
      {selectedExpo && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-sm w-full p-6 text-center relative border border-gray-100">
            <button onClick={() => setSelectedExpo(null)} className="absolute right-4 top-4 hover:bg-gray-100 rounded-full p-1 text-gray-500">
              <CloseIcon />
            </button>
            <h2 className="text-lg font-bold text-gray-800 mb-1">{selectedExpo.expoName}</h2>
            <p className="text-xs text-gray-500 mb-4">{selectedExpo.location || 'APD Stall'}</p>

            {/* Offline Canvas Render (Visible inside View Modal) */}
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 inline-block mb-4 shadow-inner">
              <QRCodeCanvas
                value={selectedExpo.qrCodeUrl || ''}
                size={220}
                level={"H"}
                includeMargin={true}
              />
            </div>

            <p className="text-xs text-gray-400 max-w-xs mx-auto mb-6">
              Scan this QR code from any smartphone camera to open the public lead capture form on your stall.
            </p>

            <button
              onClick={() => downloadQrCode(selectedExpo)}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition-colors shadow-sm"
            >
              <FaDownload /> Download High-Res PNG
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpoManagement;